import { getCustomDatabaseItems } from '@/services/axios/data.api';
import { getUserDatabases } from '@/services/userTable';

let cachedSkillSuggestions = [];
let cachedSkillMetadataMap = {};
let hasLoadedSkillSuggestions = false;
let skillSuggestionsInFlightPromise = null;

export const getAISkillTables = (databases = []) => {
  return (databases || [])
    .filter((database) => {
      const name = (database?.name || '').toLowerCase();
      const type = (database?.type || '').toLowerCase();
      const tabletype = (database?.tabletype || '').toLowerCase();
      const caption = (database?.caption || '').toLowerCase();
      return (
        name.endsWith('_ai_skils') ||
        name.endsWith('_ai_skills') ||
        name.endsWith('-ai-skills') ||
        type === 'custom-ai-skills' ||
        tabletype === 'custom-ai-skills' ||
        caption === 'ai-skills'
      );
    })
    .map((database) => database.name)
    .filter(Boolean);
};

export const getCachedAISkillSuggestions = () => {
  return Array.isArray(cachedSkillSuggestions)
    ? [...cachedSkillSuggestions]
    : [];
};

export const getCachedAISkillMetadataMap = () => {
  return { ...(cachedSkillMetadataMap || {}) };
};

const normalizeSkillContext = (rawContext) => {
  if (!rawContext) return '';
  if (typeof rawContext === 'string') return rawContext.trim();
  if (typeof rawContext === 'object') {
    const htmlContent = rawContext?.props?.htmlContent;
    if (typeof htmlContent === 'string' && htmlContent.trim()) {
      return htmlContent.trim();
    }
    const jsonContent = rawContext?.props?.jsonContent;
    if (jsonContent) {
      try {
        return JSON.stringify(jsonContent);
      } catch (error) {
        return '';
      }
    }
    try {
      return JSON.stringify(rawContext);
    } catch (error) {
      return '';
    }
  }
  return '';
};

export const fetchAISkillSuggestions = async (
  databaseList = [],
  options = {}
) => {
  const force = Boolean(options?.force);

  if (!force && hasLoadedSkillSuggestions) {
    return getCachedAISkillSuggestions();
  }

  if (!force && skillSuggestionsInFlightPromise) {
    return skillSuggestionsInFlightPromise;
  }

  skillSuggestionsInFlightPromise = (async () => {
    try {
      let aiSkillTables = getAISkillTables(databaseList);
      if (!aiSkillTables.length) {
        const databaseRes = await getUserDatabases();
        aiSkillTables = getAISkillTables(databaseRes?.data || []);
      }
      if (!aiSkillTables.length) {
        cachedSkillSuggestions = [];
        cachedSkillMetadataMap = {};
        hasLoadedSkillSuggestions = true;
        return [];
      }

      const responses = await Promise.all(
        aiSkillTables.map((tableName) =>
          getCustomDatabaseItems(tableName, '?keys=skillname,skillcontext')
        )
      );

      const allSkillItems = responses
        .flatMap((response) =>
          Array.isArray(response?.data?.items) ? response.data.items : []
        );

      const metadataMap = {};
      const allSkillNames = allSkillItems
        .map((item) => {
          const skillName = item?.skillname;
          if (typeof skillName !== 'string' || !skillName.trim()) return '';
          const normalizedName = skillName.trim();
          const lookupKey = normalizedName.toLowerCase();
          metadataMap[lookupKey] = {
            name: normalizedName,
            context: normalizeSkillContext(item?.skillcontext),
            databaseName: '',
          };
          return normalizedName;
        })
        .filter(Boolean);

      cachedSkillSuggestions = [...new Set(allSkillNames)];
      cachedSkillMetadataMap = metadataMap;
      hasLoadedSkillSuggestions = true;
      return getCachedAISkillSuggestions();
    } catch (error) {
      cachedSkillSuggestions = [];
      cachedSkillMetadataMap = {};
      hasLoadedSkillSuggestions = false;
      return [];
    } finally {
      skillSuggestionsInFlightPromise = null;
    }
  })();

  return skillSuggestionsInFlightPromise;
};
